import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import api from "../../utils/api";


// --- Icons (same as before) ---
const TrashIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 md:h-5 md:w-5 ${className}`} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const PlusIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 md:h-5 md:w-5 ${className}`} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);

const CheckIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 md:h-5 md:w-5 ${className}`} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const EditIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 md:h-5 md:w-5 ${className}`} viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const SelectIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 md:h-5 md:w-5 ${className}`} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
)

const BackIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 md:h-6 md:w-6 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const floorOptions = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Rooftop", "Garden Level", "Mezzanine"];

// Helper row creator
const createNewEntryRow = () => ({
  id: uuidv4(),
  name: "",
  description: "",
  seating_capacity: "2",
  floor: "",
  is_active: 1
});

const TableScreen = () => {
  const navigate = useNavigate();

  const isStaff = window.location.pathname.includes("/restaurant-staff/");
  const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
  const [formRows, setFormRows] = useState([createNewEntryRow()]);
  const [tableRows, setTableRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingTable, setEditingTable] = useState(null);
  const [selectedTables, setSelectedTables] = useState(new Set());
const [selectAll, setSelectAll] = useState(false);
const [deleteLoading, setDeleteLoading] = useState(false);



  // Fetch all tables on component mount
  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${apiPrefix}/restaurant/table/all`);
      // Handle both array response and nested data response
      const tables = Array.isArray(response.data) ? response.data : (response?.data || []);
      setTableRows(tables || []);
    } catch (err) {
      console.error("Error fetching tables:", err);
      setError("Failed to load tables");
      setTableRows([]); // Ensure tableRows is always an array
    } finally {
      setLoading(false);
    }
  };

  // Add selection handlers
const handleSelectTable = (tableId) => {
  setSelectedTables(prev => {
    const newSelection = new Set(prev);
    if (newSelection.has(tableId)) {
      newSelection.delete(tableId);
    } else {
      newSelection.add(tableId);
    }
    return newSelection;
  });
};

const handleSelectAll = () => {
  if (selectAll) {
    setSelectedTables(new Set());
    setSelectAll(false);
  } else {
    const allIds = tableRows.map(table => table.id);
    setSelectedTables(new Set(allIds));
    setSelectAll(true);
  }
};

const handleClearSelection = () => {
  setSelectedTables(new Set());
  setSelectAll(false);
};

// Add multi-delete handler
const handleMultiDelete = async () => {
  if (selectedTables.size === 0) {
    setError("Please select at least one table to delete");
    return;
  }

  if (!window.confirm(`Are you sure you want to delete ${selectedTables.size} table(s)? This action cannot be undone.`)) {
    return;
  }

  setDeleteLoading(true);
  try {
    const tableIds = Array.from(selectedTables);
    
    // Call bulk delete API
    const response = await api.post(`${apiPrefix}/restaurant/table/delete`, {
      ids: tableIds
    });

    // Remove deleted tables from local state
    setTableRows(prev => prev.filter(table => !selectedTables.has(table.id)));
    
    setSuccess(`Successfully deleted ${selectedTables.size} table(s)`);
    setSelectedTables(new Set());
    setSelectAll(false);
    setTimeout(() => setSuccess(""), 5000);
  } catch (err) {
    console.error("Error deleting tables:", err);
    setError("Failed to delete selected tables");
  } finally {
    setDeleteLoading(false);
  }
};




  // --- Handlers ---
  const handleRowChange = (id, e) => {
    const { name, value } = e.target;
    setFormRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
    );
    if (error) setError("");
  };

  const handleAddRow = () => {
    setFormRows([...formRows, createNewEntryRow()]);
  };

  const handleRemoveRow = (id) => {
    if (formRows.length <= 1) return;
    setFormRows(formRows.filter((row) => row.id !== id));
  };

 
  const handleEditTable = (table) => {
    setEditingTable(table);
    // Pre-fill form with table data for editing
    setFormRows([{
      id: uuidv4(),
      name: table.name || "",
      description: table.description || "",
      seating_capacity: table.seating_capacity?.toString() || "2",
      floor: table.floor || "",
      is_active: table.is_active || 1
    }]);
  };

  const handleUpdateTable = async () => {
    if (!editingTable) return;
    
    setError("");
    setSuccess("");

    const row = formRows[0];
    if (!row.name || !row.seating_capacity || !row.floor) {
      setError("Name, seating capacity, and floor are required");
      return;
    }

    try {
      const updateData = {
        id: editingTable.id,
        name: row.name,
        description: row.description,
        seating_capacity: parseInt(row.seating_capacity),
        floor: row.floor,
        is_active: parseInt(row.is_active)
      };

      await api.put(`${apiPrefix}/restaurant/table/update`, updateData);
      
      // Update local state
      setTableRows(prev => prev.map(table => 
        table.id === editingTable.id ? { ...table, ...updateData } : table
      ));
      
      setSuccess("Table updated successfully!");
      setEditingTable(null);
      fetchTables();
      setFormRows([createNewEntryRow()]);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating table:", err);
      setError("Failed to update table");
    }
  };

  const handleSubmitAll = async () => {
    setError("");
    setSuccess("");
    
    const existingTableNames = new Set(
      tableRows.map((r) => r.name?.toLowerCase()).filter(Boolean)
    );
    const pendingTableNames = new Set();

    // Validation
    for (const row of formRows) {
      if (!row.name || !row.seating_capacity || !row.floor) {
        setError("Name, seating capacity, and floor are required for all tables");
        return;
      }

      const lowerCaseName = row.name.toLowerCase();

      if (pendingTableNames.has(lowerCaseName)) {
        setError(`Duplicate table name "${row.name}" in form`);
        return;
      }
      pendingTableNames.add(lowerCaseName);

      if (existingTableNames.has(lowerCaseName)) {
        setError(`Table name "${row.name}" already exists`);
        return;
      }
    }

    try {
      const tablesToCreate = formRows.map(row => ({
        name: row.name,
        description: row.description || "",
        seating_capacity: parseInt(row.seating_capacity),
        floor: row.floor,
        is_active: parseInt(row.is_active)
      }));

      // Send batch create request - send as array directly
      const response = await api.post(`${apiPrefix}/restaurant/table/new`, tablesToCreate);
      
      // Handle response - it should return the created tables array
      const newTables = Array.isArray(response.data) ? response.data : (response?.data || []);
      
      // Update local state with new tables
      setTableRows(prev => [...prev, ...newTables]);
      setFormRows([createNewEntryRow()]);
      setSuccess(`${newTables.length} table(s) created successfully!`);
      fetchTables();
      setTimeout(() => {
        setSuccess("");
        navigate("/restaurant/table-selection");
      }, 1000);
    } catch (err) {
      console.error("Error creating tables:", err);
      setError("Failed to create tables. Please try again.");
    }
  };

  const cancelEdit = () => {
    setEditingTable(null);
    setFormRows([createNewEntryRow()]);
    setError("");
  };

  // Safe calculation functions with null checks
  const totalTables = tableRows?.length || 0;
  const totalSeats = tableRows?.reduce((sum, row) => {
    const capacity = parseInt(row?.seating_capacity) || 0;
    return sum + capacity;
  }, 0) || 0;
  
  const activeTables = tableRows?.filter(row => row?.is_active === 1).length || 0;
  
  const floorsSummary = tableRows?.reduce((acc, row) => {
    if (row?.floor) {
      acc[row.floor] = (acc[row.floor] || 0) + 1;
    }
    return acc;
  }, {}) || {};

  return (
    <div className="min-h-screen mb-12 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-start justify-center md:p-3 md:p-6">
      <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-2xl md:rounded-3xl p-2 md:p-8 w-full max-w-7xl border border-white/20 relative">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/restaurant/table-selection')}
          className="absolute top-3 left-3 md:top-6 md:left-6 flex items-center justify-center p-2 md:p-2 bg-white hover:bg-gray-50 text-gray-700 rounded-full md:rounded-xl shadow hover:shadow-md transition-all duration-200 border border-gray-200 group z-10"
          title="Back to Table Selection"
        >
          <BackIcon className="group-hover:-translate-x-1 transition-transform duration-200 text-gray-600 group-hover:text-primary" />
          <span className="hidden md:block ml-1 pr-1 font-medium text-gray-600 group-hover:text-primary">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-6 md:mb-8 mt-12 md:mt-0">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-primary to-primary rounded-xl md:rounded-2xl shadow-lg mb-3 md:mb-4">
            <span className="text-xl md:text-2xl">🍽️</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-primary mb-2">
            {editingTable ? "Edit Table" : "Table Management"}
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            {editingTable ? "Update table details" : "Add and manage your restaurant tables efficiently"}
          </p>
        </div>

        {/* Stats Summary */}
        {tableRows.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-gray-100">
              <div className="text-lg md:text-2xl font-bold text-blue-600">{totalTables}</div>
              <div className="text-xs md:text-sm text-gray-600">Total Tables</div>
            </div>
            <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-gray-100">
              <div className="text-lg md:text-2xl font-bold text-green-600">{totalSeats}</div>
              <div className="text-xs md:text-sm text-gray-600">Total Seats</div>
            </div>
            <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-gray-100">
              <div className="text-lg md:text-2xl font-bold text-orange-600">{activeTables}</div>
              <div className="text-xs md:text-sm text-gray-600">Active Tables</div>
            </div>
            <div className="bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-gray-100">
              <div className="text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">Floors</div>
              <div className="text-lg md:text-xl font-bold text-purple-600">{Object.keys(floorsSummary).length}</div>
            </div>
          </div>
        )}

        {/* --- Form Section --- */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl md:rounded-2xl p-4 md:p-2 shadow-lg border border-blue-100 mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800">
              {editingTable ? `Editing: ${editingTable.name}` : "Add New Tables"}
            </h2>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-medium self-start sm:self-auto">
              {formRows.length} row(s)
            </span>
          </div>

          <div className="space-y-3 md:space-y-4">
            {formRows.map((row, index) => (
              <div
                key={row.id}
                className="grid grid-cols-1 gap-2 md:gap-3 items-start p-3 md:p-4 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md"
              >
              <div className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-3 w-full items-center"> {/* Added items-center */}
  <div className="col-span-2 md:col-span-3 relative">
    <label className="block text-xs text-gray-500 mb-1">Table Name *</label>
    <input
      name="name"
      value={row.name}
      onChange={(e) => handleRowChange(row.id, e)}
      type="text"
      placeholder="Table 1A"
      className="w-full px-2 py-1 md:px-3 md:py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    />
  </div>
  
  <div className="col-span-1 md:col-span-2 relative">
    <label className="block text-xs text-gray-500 mb-1">Seats *</label>
    <input
      name="seating_capacity"
      value={row.seating_capacity}
      onChange={(e) => handleRowChange(row.id, e)}
      type="number"
      min="1"
      max="20"
      placeholder="4"
      className="w-full px-2 py-1 md:px-3 md:py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    />
  </div>
  
  <div className="col-span-2 md:col-span-3 relative">
    <label className="block text-xs text-gray-500 mb-1">Floor *</label>
    <select
      name="floor"
      value={row.floor}
      onChange={(e) => handleRowChange(row.id, e)}
      className="w-full px-2 py-1 md:px-3 md:py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    >
      <option value="">Select Floor</option>
      {floorOptions.map((f) => (
        <option key={f} value={f}>
          {f}
        </option>
      ))}
    </select>
  </div>
  
  <div className="col-span-2 md:col-span-2 relative">
    <label className="block text-xs text-gray-500 mb-1">Description</label>
    <input
      name="description"
      value={row.description}
      onChange={(e) => handleRowChange(row.id, e)}
      type="text"
      placeholder="Window view, outdoor seating..."
      className="w-full px-2 py-1 md:px-3 md:py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    />
  </div>
  
  <div className="col-span-1 md:col-span-2 relative">
    <label className="block text-xs text-gray-500 mb-1">Status</label>
    <select
      name="is_active"
      value={row.is_active}
      onChange={(e) => handleRowChange(row.id, e)}
      className="w-full px-2 py-1 md:px-3 md:py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    >
      <option value={1}>Active</option>
      <option value={0}>Inactive</option>
    </select>
  </div>

  <div className="col-span-2 md:col-span-12 flex items-center justify-end w-full"> {/* Simplified and centered */}
    {formRows.length > 1 && (
      <button
        onClick={() => handleRemoveRow(row.id)}
        className="text-red-500 hover:text-red-700 p-1 md:p-2 rounded-full hover:bg-red-50 transition-all duration-200 transform hover:scale-110"
        title="Remove row"
      >
        <TrashIcon />
      </button>
    )}
  </div>
</div>
              </div>
            ))}
          </div>

          {/* --- Actions --- */}
          <div className="flex flex-col gap-3 md:gap-4 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-between items-stretch sm:items-center">
              {!editingTable ? (
                <button
                  onClick={handleAddRow}
                  className="flex items-center justify-center gap-1 md:gap-2 text-primary font-semibold py-2 px-3 md:px-4 rounded-lg transition-all duration-200 border border-primary text-sm md:text-base"
                >
                  <PlusIcon /> Add Another Row
                </button>
              ) : (
                <button
                  onClick={cancelEdit}
                  className="flex items-center justify-center gap-1 md:gap-2 text-gray-600 font-semibold py-2 px-3 md:px-4 rounded-lg transition-all duration-200 border border-gray-300 text-sm md:text-base"
                >
                  Cancel Edit
                </button>
              )}
              
              <div className="flex-1 max-w-md">
                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-xs md:text-sm font-medium bg-red-50 px-3 py-2 md:px-4 md:py-2 rounded-lg border border-red-200 animate-pulse">
                    <span>⚠️</span> {error}
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-2 text-green-600 text-xs md:text-sm font-medium bg-green-50 px-3 py-2 md:px-4 md:py-2 rounded-lg border border-green-200">
                    <CheckIcon className="text-green-500" /> {success}
                  </div>
                )}
              </div>
              
              {editingTable ? (
                <button
                  onClick={handleUpdateTable}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold px-4 py-2 md:px-8 md:py-3 rounded-lg active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-1 md:gap-2 text-sm md:text-base"
                >
                  <CheckIcon /> Update Table
                </button>
              ) : (
                <button
                  onClick={handleSubmitAll}
                  className="bg-gradient-to-r from-primary to-primary text-white font-bold px-4 py-2 md:px-8 md:py-3 rounded-lg active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-1 md:gap-2 text-sm md:text-base"
                >
                  <CheckIcon /> Create Tables
                </button>
              )}
            </div>
          </div>
        </div>

        {/* --- Final Table --- */}
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg border border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 md:mb-6">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800">Table Inventory</h3>
            <div className="flex gap-2">
              <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs md:text-sm font-medium">
                {totalTables} tables
              </span>
              <button
                onClick={fetchTables}
                className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-medium hover:bg-blue-200 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>


      
{selectedTables.size > 0 && (
  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
    <div className="flex items-center gap-2">
      <span className="text-yellow-800 font-medium">
        {selectedTables.size} table(s) selected
      </span>
    </div>
    <div className="flex gap-2">
      <button
        onClick={handleClearSelection}
        className="px-3 py-1 text-yellow-700 border border-yellow-400 rounded text-sm hover:bg-yellow-100 transition-colors"
      >
        Clear Selection
      </button>
      <button
        onClick={handleMultiDelete}
        disabled={deleteLoading}
        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
      >
        {deleteLoading ? (
          <>
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
            Deleting...
          </>
        ) : (
          <>
            <TrashIcon className="h-3 w-3" />
            Delete Selected
          </>
        )}
      </button>
    </div>
  </div>
)}
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading tables...</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg md:rounded-xl border border-gray-200 shadow-sm">
              <table className="w-full text-left min-w-[600px] md:min-w-full">
                <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
                  <tr>
                    <th className="px-3 py-2 md:px-4 md:py-3 font-semibold text-slate-700 text-xs md:text-sm w-10">
  <input
    type="checkbox"
    checked={selectAll}
    onChange={handleSelectAll}
    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
  />
</th>

                    <th className="px-3 py-2 md:px-6 md:py-4 font-semibold text-slate-700 text-xs md:text-sm">Table Name</th>
                    <th className="px-3 py-2 md:px-6 md:py-4 font-semibold text-slate-700 text-xs md:text-sm">Seats</th>
                    <th className="px-3 py-2 md:px-6 md:py-4 font-semibold text-slate-700 text-xs md:text-sm">Floor</th>
                    <th className="px-3 py-2 md:px-6 md:py-4 font-semibold text-slate-700 text-xs md:text-sm hidden md:table-cell">Description</th>
                    <th className="px-3 py-2 md:px-6 md:py-4 font-semibold text-slate-700 text-xs md:text-sm">Status</th>
                    <th className="px-3 py-2 md:px-6 md:py-4 font-semibold text-slate-700 text-xs md:text-sm text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tableRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 md:px-6 md:py-12 text-center">
                        <div className="text-gray-400 mb-2 text-2xl">📊</div>
                        <div className="text-gray-500 font-medium text-sm md:text-base">No tables found</div>
                        <p className="text-xs md:text-sm text-gray-400 mt-1">Start by adding tables above</p>
                      </td>
                    </tr>
                  ) : (
                    tableRows.map((row) => (
                      <tr
                        key={row.id}
                         className={`hover:bg-blue-50/50 transition-colors duration-150 ${
    selectedTables.has(row.id) ? 'bg-blue-50' : ''
  }`}
                      >
                        <td className="px-3 py-2 md:px-4 md:py-3">
  <input
    type="checkbox"
    checked={selectedTables.has(row.id)}
    onChange={() => handleSelectTable(row.id)}
    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
  />
</td>
                        <td className="px-3 py-2 md:px-6 md:py-4">
                          <span className="font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-md text-xs md:text-sm">
                            {row.name || "Unnamed"}
                          </span>
                        </td>
                        <td className="px-3 py-2 md:px-6 md:py-4 font-medium text-gray-700 text-sm md:text-base">
                          {row.seating_capacity || 0}
                        </td>
                        <td className="px-3 py-2 md:px-6 md:py-4">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-xs md:text-sm">
                            {row.floor || "Unknown"}
                          </span>
                        </td>
                        <td className="px-3 py-2 md:px-6 md:py-4 text-gray-600 max-w-xs truncate text-sm md:text-base hidden md:table-cell">
                          {row.description || "-"}
                        </td>
                        <td className="px-3 py-2 md:px-6 md:py-4">
                          <span className={`px-2 py-1 rounded-md text-xs md:text-sm ${
                            row.is_active === 1 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }`}>
                            {row.is_active === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-3 py-2 md:px-6 md:py-4 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEditTable(row)}
                              className="text-blue-400 hover:text-blue-600 p-1 md:p-2 rounded-full hover:bg-blue-50 transition-all duration-200 transform hover:scale-110"
                              title="Edit table"
                            >
                              <EditIcon />
                            </button>
                            {/* <button
                              onClick={() => handleDeleteFromTable(row.id)}
                              className="text-red-400 hover:text-red-600 p-1 md:p-2 rounded-full hover:bg-red-50 transition-all duration-200 transform hover:scale-110"
                              title="Delete table"
                            >
                              <TrashIcon />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableScreen;